import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, AlertTriangle, Check, X } from 'lucide-react';
import { cn, getSeverityColor, getSeverityBg, getCategoryIcon, API_BASE_URL } from '../../lib/utils';
import { useAppDispatch, useAppSelector } from '../../store';
import { respondToDispatch } from '../../store/slices/dispatchSlice';

const sendResponse = async (id: string, status: 'accepted' | 'rejected', respondedAt: string, token: string | null, note?: string) => {
  try {
    await fetch(`${API_BASE_URL}/dispatches/${id}/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status, respondedAt, note }),
    });
  } catch { /* ignore */ }
};

export default function MdtDispatchAlert() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { activeDispatch } = useAppSelector((state) => state.dispatch);
  const { user, token } = useAppSelector((state) => state.auth);
  const [rejectionNote, setRejectionNote] = useState('');

  // Play alert tone when a new pending dispatch arrives
  useEffect(() => {
    if (activeDispatch?.status === 'pending') {
      // Vibration pattern for reliable feedback
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200, 100, 200]);
      }

      try {
        // Try Web Audio API
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          const ctx = new AudioContextClass();
          
          const playTone = (freq: number, duration: number) => {
            try {
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.frequency.value = freq;
              osc.type = 'square';
              gain.gain.setValueAtTime(0.3, ctx.currentTime);
              gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
              osc.start(ctx.currentTime);
              osc.stop(ctx.currentTime + duration);
            } catch {
              // Ignore individual tone errors
            }
          };

          // Play pattern immediately
          playTone(880, 0.15);
          setTimeout(() => playTone(660, 0.15), 200);
          setTimeout(() => playTone(880, 0.15), 400);
          setTimeout(() => playTone(660, 0.15), 600);

          // Repeat
          const timeout = setTimeout(() => {
            playTone(880, 0.15);
            setTimeout(() => playTone(660, 0.15), 200);
            setTimeout(() => playTone(880, 0.15), 400);
            setTimeout(() => playTone(660, 0.15), 600);
          }, 1200);

          return () => {
            clearTimeout(timeout);
            try { ctx.close(); } catch {}
          };
        }
      } catch {
        // Audio not available, vibration already handled
      }
    }
  }, [activeDispatch?.id, activeDispatch?.status]);

  if (!activeDispatch || activeDispatch.status !== 'pending') return null;

  const d = activeDispatch;

  const handleAccept = async () => {
    const respondedAt = new Date().toISOString();
    dispatch(respondToDispatch({ id: d.id, status: 'accepted', respondedAt }));
    // Backend handles unit assignment + status change when it receives the response
    sendResponse(d.id, 'accepted', respondedAt, token);

    // Small delay to ensure accept signal is sent before navigation
    await new Promise(resolve => setTimeout(resolve, 500));

    // Navigate to radar map for navigation to incident (use navigate to preserve Redux state)
    navigate('/mdt/map?radar=true');

    // Add incident log entry for acceptance
    try {
      const resourceStr = localStorage.getItem('c2_mdt_resource');
      const resource = resourceStr ? JSON.parse(resourceStr) : null;
      const resourceName = resource?.name || user?.badgeNumber || 'Unknown Unit';

      await fetch(`${API_BASE_URL}/incidents/${d.incidentId}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          details: `${resourceName} accepted dispatch via MDT`,
          action: 'Dispatch Accepted',
        }),
      });
    } catch { /* ignore */ }
  };

  const handleReject = async () => {
    const respondedAt = new Date().toISOString();
    dispatch(respondToDispatch({ id: d.id, status: 'rejected', respondedAt }));
    sendResponse(d.id, 'rejected', respondedAt, token, rejectionNote || undefined);

    // Add incident log entry for rejection with note
    try {
      const resourceStr = localStorage.getItem('c2_mdt_resource');
      const resource = resourceStr ? JSON.parse(resourceStr) : null;
      const resourceName = resource?.name || user?.badgeNumber || 'Unknown Unit';

      const details = rejectionNote
        ? `${resourceName} rejected dispatch via MDT / Note added: ${rejectionNote}`
        : `${resourceName} rejected dispatch via MDT`;

      await fetch(`${API_BASE_URL}/incidents/${d.incidentId}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          details,
          action: 'Dispatch Rejected',
        }),
      });
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex flex-col bg-background/95 backdrop-blur-md animate-in fade-in duration-300">
      {/* Pulsing border top */}
      <div className="h-1 bg-red-500 animate-pulse" />

      {/* Alert Header */}
      <div className="flex items-center justify-center gap-2 py-3 bg-red-500/10">
        <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />
        <span className="text-sm font-extrabold text-red-400 uppercase tracking-widest">
          Incoming Dispatch
        </span>
        <AlertTriangle className="w-5 h-5 text-red-400 animate-pulse" />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6">
        {/* Incident Info Card */}
        <div className="w-full max-w-sm bg-card border-2 border-border rounded-2xl overflow-hidden shadow-2xl">
          {/* Severity bar */}
          <div
            className={cn(
              'py-2 px-4 text-center text-xs font-extrabold uppercase tracking-wider',
              d.incidentSeverity === 'critical'
                ? 'bg-red-500/20 text-red-400'
                : d.incidentSeverity === 'high'
                ? 'bg-orange-500/20 text-orange-400'
                : d.incidentSeverity === 'medium'
                ? 'bg-yellow-500/20 text-yellow-400'
                : 'bg-blue-500/20 text-blue-400'
            )}
          >
            {d.incidentSeverity} Priority
          </div>

          <div className="p-5 space-y-4">
            {/* Category + ID */}
            <div className="flex items-center gap-3">
              <span className="text-3xl">{getCategoryIcon(d.incidentCategory)}</span>
              <div>
                <span className="text-[10px] font-mono text-muted-foreground block">{d.incidentId}</span>
                <span
                  className={cn(
                    'text-[10px] px-1.5 py-0.5 rounded-full border font-bold uppercase inline-block',
                    getSeverityBg(d.incidentSeverity),
                    getSeverityColor(d.incidentSeverity)
                  )}
                >
                  {d.incidentSeverity}
                </span>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-lg font-bold text-foreground leading-tight">
              {d.incidentTitle}
            </h2>

            {/* Location */}
            <div className="flex items-start gap-2 p-3 rounded-xl bg-accent/50 border border-border">
              <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  {d.incidentLocation.address || 'Location pending'}
                </p>
                {d.incidentLocation.latitude && (
                  <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
                    {d.incidentLocation.latitude.toFixed(4)}, {d.incidentLocation.longitude.toFixed(4)}
                  </p>
                )}
              </div>
            </div>

            {/* Notes */}
            {d.notes && (
              <div className="p-3 rounded-xl bg-yellow-500/5 border border-yellow-500/20">
                <p className="text-xs text-foreground">{d.notes}</p>
              </div>
            )}

            {/* Dispatched by */}
            <p className="text-[10px] text-muted-foreground text-center">
              Dispatched by <strong>{d.dispatchedBy}</strong> to{' '}
              <strong className="text-primary">{d.targetUnitCallSign}</strong>
            </p>
          </div>
        </div>
      </div>

      {/* Accept / Reject Buttons — fixed at bottom */}
      <div className="px-6 pb-8 pt-4 space-y-3 flex-shrink-0">
        <button
          onClick={handleAccept}
          className="w-full py-4 rounded-2xl bg-green-500 text-white font-extrabold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg shadow-green-500/30"
        >
          <Check className="w-6 h-6" />
          ACCEPT ASSIGNMENT
        </button>

        {/* Rejection note input */}
        <textarea
          value={rejectionNote}
          onChange={(e) => setRejectionNote(e.target.value)}
          placeholder="Reason for rejection (optional)..."
          rows={2}
          className="w-full px-4 py-3 rounded-xl bg-card border-2 border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-red-500/50 resize-none"
        />

        <button
          onClick={handleReject}
          className="w-full py-3 rounded-2xl bg-card border-2 border-red-500/30 text-red-400 font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
        >
          <X className="w-5 h-5" />
          Reject
        </button>
      </div>
    </div>
  );
}
